import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Box, 
  Grid, 
  Alert, 
  MenuItem, 
  CircularProgress, 
  FormControl, 
  InputLabel, 
  Select,
  Card,
  CardContent,
  Switch,
  Tooltip,
  Container as MuiContainer,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Code as CodeIcon, PlayArrow, Clear, Refresh, Storage as DatabaseIcon } from '@mui/icons-material';
import { 
  module2Service, 
  CodeGenerationRequest, 
  CodeGenerationStatus, 
  CodeExecutionRequest, 
  CodeExecutionResult,
  useGenerateCodeDb,
  useGetCodeGenerationStatusDb,
  useGetCodeDb,
  useExecuteCodeDb,
  useGetExecutionStatusDb
} from '../services';
import { useApp } from '../context/AppContext';
import { useDatabaseMode } from '../context/DatabaseModeContext';
import apiClient from '../services/api/client';
import { workflowService } from '../services/api/workflowService';
import WorkflowStepper from '@/components/WorkflowStepper';
import { getWorkflowStatus } from '@/utils/moduleStatus';
import { Header } from '@/components/shared/Header';

// Define file interface
interface BlobFile {
  name: string;
  path: string;
  size: number;
  lastModified: string;
  contentType: string;
}

// New interface for file option dropdowns
interface FileOption {
  label: string;
  value: string;
  path: string;
}

const Module2CodeGeneration = () => {
  const { serviceStatus, workflowData, updateWorkflowData, workflowId: contextWorkflowId, setWorkflowId } = useApp();
  const { isUsingDatabaseApproach } = useDatabaseMode();
  const navigate = useNavigate();
  const { clientId, workflowId: urlWorkflowId } = useParams();
  
  // Use URL parameters if available, otherwise use context
  const effectiveWorkflowId = urlWorkflowId || contextWorkflowId;
  
  // Add state for WorkflowStepper collapse
  const [stepperCollapsed, setStepperCollapsed] = useState(() => {
    return localStorage.getItem('module2_stepper_collapsed') === 'true';
  });
  
  // Helper function to get Application Insights
  const getAppInsights = () => {
    return (window as any).__appInsights || null;
  };
  
  // Track page view in Application Insights
  useEffect(() => {
    const appInsights = getAppInsights();
    if (appInsights) {
      appInsights.trackPageView({
        name: 'Module2CodeGeneration',
        uri: window.location.pathname,
        properties: {
          workflowId: effectiveWorkflowId || 'none',
          clientId: clientId || 'none'
        }
      });
    }
  }, [effectiveWorkflowId, clientId]);
  
  // Default prompt text based on the workflow context
  const getDefaultPrompt = () => {
    // If we have workflow data, we can reference it
    if (workflowData && Object.keys(workflowData).length > 0) {
      return `Generate a keyword classification function that:
1. Takes a keyword string as input
2. Evaluates it against key patterns identified in workflow ${effectiveWorkflowId || 'current'}
3. Returns the classification category and confidence score
4. Handles both exact and fuzzy matching
5. Includes error handling for edge cases`;
    }
    
    // Generic default if no workflow data
    return `Generate a keyword classification function that:
1. Takes a keyword string as input
2. Evaluates it against defined pattern rules
3. Returns the classification category and confidence score
4. Handles both exact and fuzzy matching
5. Includes error handling for edge cases`;
  };
  
  // Standard approach state
  const [codePrompt, setCodePrompt] = useState(() => getDefaultPrompt());
  const [language, setLanguage] = useState<'python' | 'javascript'>('python');
  const [framework, setFramework] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [executionResult, setExecutionResult] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  
  // File selection state
  const [stemWordFiles, setStemWordFiles] = useState<FileOption[]>([]);
  const [keywordFiles, setKeywordFiles] = useState<FileOption[]>([]);
  const [selectedStemWordFile, setSelectedStemWordFile] = useState<string>('');
  const [selectedKeywordFile, setSelectedKeywordFile] = useState<string>('');
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [customStemWordsPath, setCustomStemWordsPath] = useState('');
  const [customKeywordsPath, setCustomKeywordsPath] = useState('');
  
  // Database approach is now managed by context
  const [activeModuleId, setActiveModuleId] = useState<number | null>(() => {
    // Try to load from localStorage
    if (effectiveWorkflowId) {
      const storedId = localStorage.getItem(`module2_${effectiveWorkflowId}_module_id`);
      return storedId ? parseInt(storedId, 10) : null;
    }
    return null;
  });
  const [dbWorkflowId, setDbWorkflowId] = useState<number | null>(() => {
    // Try to load from localStorage
    const storedId = localStorage.getItem('db_workflow_id');
    return storedId ? parseInt(storedId, 10) : null;
  });
  
  // Database-first React Query hooks
  const generateCodeDbMutation = useGenerateCodeDb();
  const codeGenerationStatusDbQuery = useGetCodeGenerationStatusDb(activeModuleId || 0);
  const codeDbQuery = useGetCodeDb(activeModuleId || 0);
  const executeCodeDbMutation = useExecuteCodeDb();
  const executionStatusDbQuery = useGetExecutionStatusDb(activeModuleId || 0);
  
  // Update context with URL parameters if available
  useEffect(() => {
    if (urlWorkflowId && urlWorkflowId !== contextWorkflowId) {
      setWorkflowId(urlWorkflowId);
    }
  }, [urlWorkflowId, contextWorkflowId, setWorkflowId]);
  
  // Redirect if no workflow ID is provided (modify to only redirect if needed)
  useEffect(() => {
    if (!effectiveWorkflowId && !clientId) {
      navigate('/');
    }
  }, [effectiveWorkflowId, clientId, navigate]);

  // Load available files when component mounts or workflow/client changes
  useEffect(() => {
    if (effectiveWorkflowId && clientId) {
      loadAvailableFiles();
    }
  }, [effectiveWorkflowId, clientId]);
  
  // Effect to handle database-first code generation status updates
  useEffect(() => {
    // Only proceed if we're using database approach and have status data
    if (isUsingDatabaseApproach && codeGenerationStatusDbQuery.data && activeModuleId) {
      const statusData = codeGenerationStatusDbQuery.data;
      console.log('DB code generation status update:', statusData);
      
      // If the code generation is completed, fetch the code
      if (statusData.status === 'completed') {
        // The code should be available in the codeDbQuery if it's enabled
        if (codeDbQuery.data && codeDbQuery.data.code) {
          setGeneratedCode(codeDbQuery.data.code);
          setIsGenerating(false);
        }
      } else if (statusData.status === 'failed') {
        setError(`Database-first code generation failed: ${statusData.error || 'Unknown error'}`);
        setIsGenerating(false);
      }
    }
  }, [isUsingDatabaseApproach, codeGenerationStatusDbQuery.data, codeDbQuery.data, activeModuleId]);
  
  // Effect to handle database-first code execution status updates
  useEffect(() => {
    // Only proceed if we're using database approach and have execution status data
    if (isUsingDatabaseApproach && executionStatusDbQuery.data && activeModuleId) {
      const executionData = executionStatusDbQuery.data;
      console.log('DB code execution status update:', executionData);
      
      // If the execution is completed, show the results
      if (executionData.status === 'completed') {
        if (executionData.output) {
          setExecutionResult(executionData.output);
          setIsExecuting(false);
        }
      } else if (executionData.status === 'failed') {
        setError(`Database-first code execution failed: ${executionData.error || 'Unknown error'}`);
        setIsExecuting(false);
      }
    }
  }, [isUsingDatabaseApproach, executionStatusDbQuery.data, activeModuleId]);

  // Function to load available files from different directories
  const loadAvailableFiles = async () => {
    setIsLoadingFiles(true);
    setError(null);
    
    // Track file loading in Application Insights
    const appInsights = getAppInsights();
    const startTime = Date.now();
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'Module2_LoadFilesStarted',
        properties: {
          workflowId: effectiveWorkflowId || 'none',
          clientId: clientId || 'none',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    if (!clientId || !effectiveWorkflowId) {
      setError('Missing client ID or workflow ID');
      setIsLoadingFiles(false);
      
      if (appInsights) {
        appInsights.trackEvent({
          name: 'Module2_LoadFilesFailed',
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            error: 'Missing client ID or workflow ID',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return;
    }
    
    try {
      // Arrays to store the found files
      const stemWordsFiles: FileOption[] = [];
      const keywordsFiles: FileOption[] = [];
      
      // List all files for the workflow
      try {
        // Using the correct API parameters: container, client_id, workflow_id
        const response = await apiClient.get('/api/v1/storage/list', {
          params: {
            container: 'client-data',  // Correct container name
            client_id: clientId,
            workflow_id: effectiveWorkflowId
          }
        });
        
        console.log('File listing response:', response.data);
        
        if (response.data && Array.isArray(response.data.files)) {
          // Log all the files returned by the API to help diagnose issues
          console.log('All files returned by API:', response.data.files.map((f: BlobFile) => f.path));
          
          // Process all files from the response
          response.data.files.forEach((file: BlobFile) => {
            const filePath = file.path || '';
            const fileName = file.name;
            
            console.log(`Processing file: ${fileName}, path: ${filePath}`);
            
            // More inclusive check for stem words files:
            // Look for JSON files in module1/output OR outputs/module1
            if ((filePath.includes('/module1/output/') || filePath.includes('/outputs/module1/')) &&
                (fileName.endsWith('.json') || fileName.endsWith('.csv'))) {
              console.log(`Found stem word file: ${fileName}, path: ${filePath}`);
              stemWordsFiles.push({
                label: fileName,
                value: fileName,
                path: filePath
              });
            }
            
            // Check if file is in uploads or input directory (keywords)
            else if ((filePath.includes('/input/') || filePath.includes('/uploads/')) && 
                    fileName.endsWith('.csv')) {
              console.log(`Found keyword file: ${fileName}, path: ${filePath}`);
              keywordsFiles.push({
                label: fileName,
                value: fileName,
                path: filePath
              });
            } else {
              console.log(`File doesn't match criteria: ${fileName}, path: ${filePath}`);
            }
          });
        }
        
        // Filter for files with "final" in the name first
        const finalFiles = stemWordsFiles.filter((f: FileOption) => 
          f.label.toLowerCase().includes('final')
        );
        
        // Use finalFiles if available, otherwise use all stem word files
        let filesToShow = finalFiles.length > 0 ? finalFiles : stemWordsFiles;
        
        // Then sort by preference: 1. JSON files first
        filesToShow.sort((a: FileOption, b: FileOption) => {
          if (a.label.endsWith('.json') && !b.label.endsWith('.json')) return -1;
          if (!a.label.endsWith('.json') && b.label.endsWith('.json')) return 1;
          return 0;
        });
        
        console.log('Files to show after filtering for "final":', filesToShow);
        
        // Only add default files if none found
        if (stemWordsFiles.length === 0) {
          console.log('No stem word files found, adding default');
          filesToShow = [{ 
            label: 'stem_words.csv', 
            value: 'stem_words.csv', 
            path: `${clientId}/workflows/${effectiveWorkflowId}/module1/output/stem_words.csv` 
          }];
        }
        
        if (keywordsFiles.length === 0) {
          console.log('No keyword files found, adding default');
          keywordsFiles.push({ 
            label: 'keywords.csv', 
            value: 'keywords.csv', 
            path: `${clientId}/workflows/${effectiveWorkflowId}/input/keywords.csv` 
          });
        }
        
        console.log('Final stem word files:', filesToShow);
        console.log('Final keyword files:', keywordsFiles);
        
        // Set the state with the found files
        setStemWordFiles(filesToShow);
        setKeywordFiles(keywordsFiles);
        
        // Set default selections to the first item in each list
        if (filesToShow.length > 0) {
          setSelectedStemWordFile(filesToShow[0].path);
        }
        
        if (keywordsFiles.length > 0) {
          setSelectedKeywordFile(keywordsFiles[0].path);
        }
        
      } catch (err) {
        console.error('Error listing files:', err);
      }
      
      // Track success in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'Module2_LoadFilesCompleted',
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            stemWordFileCount: stemWordsFiles.length,
            keywordFileCount: keywordsFiles.length,
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
    } catch (err) {
      console.error('Error loading available files:', err);
      setError('Failed to load available files. Please try again.');
      
      // Track error in Application Insights
      if (appInsights) {
        appInsights.trackException({
          exception: err instanceof Error ? err : new Error('Unknown error loading files'),
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            operation: 'loadAvailableFiles',
            errorMessage: err instanceof Error ? err.message : String(err),
            timestamp: new Date().toISOString()
          }
        });
      }
    } finally {
      setIsLoadingFiles(false);
    }
  };
  
  const handleGenerateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Track code generation attempt in Application Insights
    const appInsights = getAppInsights();
    const startTime = Date.now();
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'Module2_GenerateCodeAttempt',
        properties: {
          workflowId: effectiveWorkflowId || 'none',
          clientId: clientId || 'none',
          stemWordFile: selectedStemWordFile,
          language: language,
          usingDatabaseApproach: isUsingDatabaseApproach,
          timestamp: new Date().toISOString()
        }
      });
    }
    
    if (!codePrompt.trim()) {
      setError('Please enter a code generation prompt');
      
      if (appInsights) {
        appInsights.trackEvent({
          name: 'Module2_GenerateCodeValidationFailed',
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            error: 'Empty prompt',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return;
    }
    
    if (!selectedStemWordFile) {
      setError('Please select a stem words file');
      
      if (appInsights) {
        appInsights.trackEvent({
          name: 'Module2_GenerateCodeValidationFailed',
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            error: 'No stem word file selected',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    setGeneratedCode(null);
    setExecutionResult(null);
    
    try {
      // Do NOT add "client-data/" prefix - the backend API validation expects paths without it
      let stemWordsBlobPath = selectedStemWordFile;
      if (stemWordsBlobPath.startsWith('client-data/')) {
        // Remove the prefix if it's there
        stemWordsBlobPath = stemWordsBlobPath.replace('client-data/', '');
        console.log('Removed client-data/ prefix from stem_words_blob to match API validation');
      }
      
      // Use static sample keywords instead of trying to fetch from the file
      // since the /api/v1/storage/read endpoint doesn't exist
      const sampleKeywords = [
        "buy shoes online",
        "best running shoes",
        "womens athletic footwear",
        "mens dress shoes",
        "kids sneakers",
        "hiking boots sale",
        "leather oxford shoes",
        "discount nike shoes",
        "adidas running shoes",
        "waterproof walking shoes"
      ];
      
      console.log(`Using ${sampleKeywords.length} static sample keywords`);
      
      // Check if we should use the database-first approach
      if (isUsingDatabaseApproach) {
        try {
          // Must have a database workflow ID
          if (!dbWorkflowId) {
            // Try to get the database workflow ID from localStorage
            const storedId = localStorage.getItem('db_workflow_id');
            if (storedId) {
              setDbWorkflowId(parseInt(storedId, 10));
            } else {
              // If no stored ID, need to create a new workflow
              // First get client database ID
              const clients = await workflowService.getDbClients();
              
              // Find the client that matches the clientId
              const client = clients.find(c => c.client_code === clientId);
              if (!client) {
                throw new Error(`No database client found with code ${clientId}`);
              }
              
              // Create a new workflow
              const newWorkflow = await workflowService.createDbWorkflow(
                client.client_id,
                `Workflow for ${clientId}`,
                `Created from Module 2 UI with database-first approach`
              );
              
              // Store and set the workflow ID
              localStorage.setItem('db_workflow_id', newWorkflow.workflow_id.toString());
              setDbWorkflowId(newWorkflow.workflow_id);
            }
          }
          
          // Now we should have a database workflow ID
          const workflowIdToUse = dbWorkflowId || parseInt(localStorage.getItem('db_workflow_id') || '0', 10);
          
          if (!workflowIdToUse) {
            throw new Error('Could not determine database workflow ID');
          }
          
          // Start the database-first code generation
          const dbResponse = await generateCodeDbMutation.mutateAsync({
            workflowId: workflowIdToUse,
            stemWordsBlob: stemWordsBlobPath,
            sampleKeywords: sampleKeywords
          });
          
          console.log('Database-first code generation response:', dbResponse);
          
          // Store the module ID for status polling
          if (dbResponse && dbResponse.module_id) {
            setActiveModuleId(dbResponse.module_id);
            localStorage.setItem(`module2_${effectiveWorkflowId}_module_id`, dbResponse.module_id.toString());
          } else {
            throw new Error('No module ID returned from database-first code generation');
          }
          
          // Status updates will be handled by the useEffect hook that watches the query
          return;
        } catch (dbError) {
          console.error('Database-first code generation error:', dbError);
          setError(`Database-first approach failed: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
          setIsUsingDatabaseApproach(false);
          localStorage.setItem('use_database_first', 'false');
          
          // Fall through to standard approach
        }
      }
      
      // If we're not using the database-first approach or it failed, use the standard approach
      // Ensure workflow_id is properly formatted (alphanumeric, underscores, hyphens)
      const validWorkflowId = effectiveWorkflowId && /^[a-zA-Z0-9_-]+$/.test(effectiveWorkflowId) 
        ? effectiveWorkflowId 
        : 'default-workflow';
      
      const request: CodeGenerationRequest = {
        workflow_id: validWorkflowId,
        stem_words_blob: stemWordsBlobPath,
        sample_keywords: sampleKeywords
      };
      
      // Make the API call regardless of service status
      console.log('Sending request:', request);
      const response = await module2Service.generateCode(request);
      
      if (response.status === 'failed') {
        setError(response.error || 'Failed to generate code');
        return;
      }
      
      // If we have a generation_id, we need to poll for status
      if (response.generation_id) {
        // Poll for status
        const maxAttempts = 30; // Maximum number of polling attempts
        const pollInterval = 2000; // 2 seconds
        let attempts = 0;
        
        const pollStatus = async () => {
          attempts++;
          const statusResponse = await module2Service.getGenerationStatus(
            response.generation_id!,
            effectiveWorkflowId || 'default'
          );
          
          if (statusResponse.status === 'completed' && statusResponse.code) {
            // If we have the code directly in the response
            setGeneratedCode(statusResponse.code);
            return;
          } else if (statusResponse.status === 'completed' && statusResponse.code_blob) {
            // If we need to fetch the code from a blob
            try {
              const codeResponse = await module2Service.getGeneratedCode(
                effectiveWorkflowId || 'default', 
                response.generation_id!
              );
              setGeneratedCode(codeResponse.code);
              return;
            } catch (err) {
              console.error('Error fetching code blob:', err);
              setError('Generated code is available but could not be fetched');
              return;
            }
          } else if (statusResponse.status === 'failed') {
            setError(statusResponse.error || 'Code generation failed');
            return;
          } else if (attempts < maxAttempts) {
            // Continue polling
            setTimeout(pollStatus, pollInterval);
          } else {
            setError('Code generation timed out. Please try again.');
          }
        };
        
        // Start polling
        setTimeout(pollStatus, pollInterval);
      } else if (response.code) {
        // If we have the code directly in the initial response
        setGeneratedCode(response.code);
      } else {
        // No generation_id and no code - possibly a synchronous completion without result
        setError('Code generation completed but no code was returned');
      }
      
      // Track successful code generation in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'Module2_GenerateCodeSuccess',
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            generationId: response.generation_id || 'unknown',
            durationMs: duration,
            hasCode: !!response.code || !!response.code_blob,
            timestamp: new Date().toISOString()
          }
        });
      }
      
    } catch (err) {
      console.error('Error generating code:', err);
      setError('Failed to generate code. Please try again.');
      
      // Track error in Application Insights
      if (appInsights) {
        appInsights.trackException({
          exception: err instanceof Error ? err : new Error('Unknown error generating code'),
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            operation: 'handleGenerateCode',
            errorMessage: err instanceof Error ? err.message : String(err),
            timestamp: new Date().toISOString()
          }
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleExecuteCode = async () => {
    // Track code execution attempt in Application Insights
    const appInsights = getAppInsights();
    const startTime = Date.now();
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'Module2_ExecuteCodeAttempt',
        properties: {
          workflowId: effectiveWorkflowId || 'none',
          clientId: clientId || 'none',
          keywordFile: selectedKeywordFile,
          usingDatabaseApproach: isUsingDatabaseApproach,
          timestamp: new Date().toISOString()
        }
      });
    }
    
    if (!generatedCode) {
      setError('No code to execute');
      
      if (appInsights) {
        appInsights.trackEvent({
          name: 'Module2_ExecuteCodeValidationFailed',
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            error: 'No code to execute',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return;
    }
    
    if (!selectedKeywordFile) {
      setError('Please select a keywords file');
      
      if (appInsights) {
        appInsights.trackEvent({
          name: 'Module2_ExecuteCodeValidationFailed',
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            error: 'No keyword file selected',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return;
    }
    
    setIsExecuting(true);
    setError(null);
    setExecutionResult(null);
    
    try {
      // Ensure keywords_blob has the 'client-data/' prefix
      let keywordsBlobPath = selectedKeywordFile;
      if (!keywordsBlobPath.startsWith('client-data/')) {
        keywordsBlobPath = `client-data/${keywordsBlobPath}`;
      }
      
      // Check if we should use the database-first approach
      if (isUsingDatabaseApproach && activeModuleId) {
        try {
          // Execute code using the database approach
          const dbResponse = await executeCodeDbMutation.mutateAsync({
            moduleId: activeModuleId,
            keywordsBlob: keywordsBlobPath
          });
          
          console.log('Database-first code execution response:', dbResponse);
          
          // Status updates will be handled by the useEffect hook that watches the query
          return;
        } catch (dbError) {
          console.error('Database-first code execution error:', dbError);
          setError(`Database-first execution failed: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
          
          // Fall through to standard approach
        }
      }
      
      // If we're not using the database-first approach or it failed, use the standard approach
      // Ensure workflow_id is properly formatted (alphanumeric, underscores, hyphens)
      const validWorkflowId = effectiveWorkflowId && /^[a-zA-Z0-9_-]+$/.test(effectiveWorkflowId) 
        ? effectiveWorkflowId 
        : 'default-workflow';
        
      // Create a generation ID in the expected format
      const mockGenerationId = 'gen-' + Math.random().toString(36).substring(2, 15);
      
      const request: CodeExecutionRequest = {
        workflow_id: validWorkflowId,
        generation_id: mockGenerationId,
        keywords_blob: keywordsBlobPath
      };
      
      // Make the API call regardless of service status
      console.log('Sending execution request:', request);
      const response = await module2Service.executeCode(request);
      
      if (response.status === 'failed') {
        setError(response.error || 'Failed to execute code');
        return;
      }
      
      // If we have an execution_id, we need to poll for status
      if (response.execution_id) {
        // Poll for status
        const maxAttempts = 30; // Maximum number of polling attempts
        const pollInterval = 1000; // 1 second
        let attempts = 0;
        
        const pollStatus = async () => {
          attempts++;
          const statusResponse = await module2Service.getExecutionStatus(
            response.execution_id,
            effectiveWorkflowId || 'default'
          );
          
          if (statusResponse.status === 'completed') {
            // If we have the output directly in the response
            if (statusResponse.output) {
              setExecutionResult(statusResponse.output);
              return;
            } else if (statusResponse.results_blobs) {
              // If we need to fetch results from blobs
              // In a real implementation, we would fetch the results from the blob storage
              // For now, we'll just show the blob names
              setExecutionResult(
                'Execution completed. Results available in:\n' + 
                Object.entries(statusResponse.results_blobs)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join('\n')
              );
              return;
            } else {
              setExecutionResult('Execution completed successfully.');
              return;
            }
          } else if (statusResponse.status === 'failed') {
            setError(statusResponse.error || 'Code execution failed');
            return;
          } else if (attempts < maxAttempts) {
            // Continue polling
            setTimeout(pollStatus, pollInterval);
          } else {
            setError('Code execution timed out. Please try again.');
          }
        };
        
        // Start polling
        setTimeout(pollStatus, pollInterval);
      } else if (response.output) {
        // If we have the output directly in the initial response
        setExecutionResult(response.output);
      } else {
        // No execution_id and no output - possibly a synchronous completion without result
        setExecutionResult('Execution completed but no output was returned');
      }
      
      // Track successful code execution in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'Module2_ExecuteCodeSuccess',
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            executionId: response.execution_id || 'unknown',
            durationMs: duration,
            status: response.status,
            hasOutput: !!response.output,
            timestamp: new Date().toISOString()
          }
        });
      }
      
    } catch (err) {
      console.error('Error executing code:', err);
      setError('Failed to execute code. Please try again.');
      
      // Track error in Application Insights
      if (appInsights) {
        appInsights.trackException({
          exception: err instanceof Error ? err : new Error('Unknown error executing code'),
          properties: {
            workflowId: effectiveWorkflowId || 'none',
            clientId: clientId || 'none',
            operation: 'handleExecuteCode',
            errorMessage: err instanceof Error ? err.message : String(err),
            timestamp: new Date().toISOString()
          }
        });
      }
    } finally {
      setIsExecuting(false);
    }
  };
  
  // Handler for WorkflowStepper collapse toggle
  const handleStepperCollapseChange = (collapsed: boolean) => {
    setStepperCollapsed(collapsed);
    localStorage.setItem('module2_stepper_collapsed', collapsed.toString());
  };

  if (!effectiveWorkflowId && !clientId) {
    return null; // Will redirect due to useEffect
  }

  return (
    <Box className="container mx-auto py-6">
      {/* Replace the existing header section with the shared Header component */}
            <Header
        title="Module 2: Code Generation"
        subtitle="Generate and execute code using the Module 2 service"
        workflowId={effectiveWorkflowId as string}
      />
      


      {/* Updated layout with WorkflowStepper integration */}
      <Grid container spacing={2} columnGap={!stepperCollapsed ? 4 : 0}>
        {/* Vertical WorkflowStepper */}
        <Grid item xs="auto">
          <WorkflowStepper
            activeStep={1}            
            workflowId={effectiveWorkflowId || ''}
            clientId={clientId}
            moduleCompletionStatus={getWorkflowStatus(effectiveWorkflowId as string)}
            onCollapseChange={handleStepperCollapseChange}
          />
        </Grid>
        
        {/* Main content */}
        <Grid item xs>
          <Grid container spacing={3}>
            {/* Input Form */}
            <Grid item xs={12} md={stepperCollapsed ? 6 : 12} lg={6}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Code Generation Configuration
                </Typography>
                
                {/* File Selection Dropdowns */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    File Selection
                  </Typography>
                  
                  <FormControl fullWidth margin="normal" size="small">
                    <InputLabel id="stem-words-file-label">Stem Words File</InputLabel>
                    <Select
                      labelId="stem-words-file-label"
                      value={selectedStemWordFile}
                      label="Stem Words File"
                      onChange={(e) => setSelectedStemWordFile(e.target.value)}
                      disabled={isLoadingFiles || stemWordFiles.length === 0}
                    >
                      {stemWordFiles.map((file) => (
                        <MenuItem key={file.value} value={file.path}>
                          {file.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {selectedStemWordFile?.endsWith('.json') ? 
                        "Using JSON file with full topic hierarchy from Module 1." : 
                        "Using CSV file for stem words (legacy format)."}
                    </Typography>
                  </FormControl>
                  
                  {/* Direct path input for Stem Words File */}
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Or enter file path directly:
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs>
                        <TextField
                          size="small"
                          fullWidth
                          label="Direct Path to Stem Words File"
                          placeholder="e.g., client-data/clientId/workflows/workflowId/outputs/module1/final_stemwords.json"
                          value={customStemWordsPath}
                          onChange={(e) => setCustomStemWordsPath(e.target.value)}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          size="medium"
                          onClick={() => {
                            if (customStemWordsPath) {
                              // Add to dropdown options if not already there
                              const fileName = customStemWordsPath.split('/').pop() || customStemWordsPath;
                              const newFile = {
                                label: fileName,
                                value: fileName,
                                path: customStemWordsPath
                              };
                              
                              const fileExists = stemWordFiles.some(file => file.path === customStemWordsPath);
                              if (!fileExists) {
                                setStemWordFiles(prev => [...prev, newFile]);
                              }
                              
                              // Select this path
                              setSelectedStemWordFile(customStemWordsPath);
                              setCustomStemWordsPath(''); // Clear input field
                            }
                          }}
                          disabled={!customStemWordsPath}
                        >
                          Use Path
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <FormControl fullWidth margin="normal" size="small">
                    <InputLabel id="keywords-file-label">Keywords File</InputLabel>
                    <Select
                      labelId="keywords-file-label"
                      value={selectedKeywordFile}
                      label="Keywords File"
                      onChange={(e) => setSelectedKeywordFile(e.target.value)}
                      disabled={isLoadingFiles || keywordFiles.length === 0}
                    >
                      {keywordFiles.map((file) => (
                        <MenuItem key={file.value} value={file.path}>
                          {file.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Direct path input for Keywords File */}
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Or enter keywords file path directly:
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs>
                        <TextField
                          size="small"
                          fullWidth
                          label="Direct Path to Keywords File"
                          placeholder="e.g., client-data/clientId/workflows/workflowId/input/keywords.csv"
                          value={customKeywordsPath}
                          onChange={(e) => setCustomKeywordsPath(e.target.value)}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          size="medium"
                          onClick={() => {
                            if (customKeywordsPath) {
                              // Add to dropdown options if not already there
                              const fileName = customKeywordsPath.split('/').pop() || customKeywordsPath;
                              const newFile = {
                                label: fileName,
                                value: fileName,
                                path: customKeywordsPath
                              };
                              
                              const fileExists = keywordFiles.some(file => file.path === customKeywordsPath);
                              if (!fileExists) {
                                setKeywordFiles(prev => [...prev, newFile]);
                              }
                              
                              // Select this path
                              setSelectedKeywordFile(customKeywordsPath);
                              setCustomKeywordsPath(''); // Clear input field
                            }
                          }}
                          disabled={!customKeywordsPath}
                        >
                          Use Path
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      size="small"
                      startIcon={<Refresh />}
                      onClick={loadAvailableFiles}
                      disabled={isLoadingFiles}
                    >
                      {isLoadingFiles ? 'Loading...' : 'Refresh Files'}
                    </Button>
                  </Box>
                </Box>
                
                <Box component="form" onSubmit={handleGenerateCode} sx={{ mt: 2 }}>
                  <TextField
                    label="Code Generation Prompt"
                    multiline
                    rows={6}
                    value={codePrompt}
                    onChange={(e) => setCodePrompt(e.target.value)}
                    placeholder="Describe the code you want to generate..."
                    fullWidth
                    required
                    margin="normal"
                    helperText="Provide a clear description of the code you want to generate"
                  />
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="language-select-label">Programming Language</InputLabel>
                        <Select
                          labelId="language-select-label"
                          value={language}
                          label="Programming Language"
                          onChange={(e) => setLanguage(e.target.value as 'python' | 'javascript')}
                        >
                          <MenuItem value="python">Python</MenuItem>
                          <MenuItem value="javascript">JavaScript</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Framework (Optional)"
                        value={framework}
                        onChange={(e) => setFramework(e.target.value)}
                        placeholder="e.g., React, Django, Spring"
                        fullWidth
                        margin="normal"
                        helperText="Specify a framework if applicable"
                      />
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setCodePrompt(getDefaultPrompt())}
                      sx={{ textTransform: 'none' }}
                    >
                      Reset to Default Prompt
                    </Button>
                  </Box>
                  
                  {error && (
                    <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isGenerating || !selectedStemWordFile}
                    fullWidth
                    startIcon={isGenerating ? <CircularProgress size={20} /> : <CodeIcon />}
                    sx={{ mt: 2 }}
                  >
                    {isGenerating ? 'Generating Code...' : 'Generate Code'}
                  </Button>
                </Box>
              </Paper>
            </Grid>
            
            {/* Results Display */}
            <Grid item xs={12} md={stepperCollapsed ? 6 : 12} lg={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Generated Code
                </Typography>
                
                {isGenerating ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={48} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Generating code...
                    </Typography>
                  </Box>
                ) : generatedCode ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box 
                      sx={{ 
                        bgcolor: 'grey.900', 
                        p: 2, 
                        borderRadius: 1, 
                        overflow: 'auto',
                        maxHeight: '300px',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        color: 'common.white',
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {generatedCode}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleExecuteCode}
                        disabled={isExecuting || !selectedKeywordFile}
                        startIcon={isExecuting ? <CircularProgress size={20} /> : <PlayArrow />}
                        sx={{ flex: 1 }}
                      >
                        {isExecuting ? 'Executing...' : 'Execute Code'}
                      </Button>
                      
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                          setGeneratedCode(null);
                          setExecutionResult(null);
                        }}
                        startIcon={<Clear />}
                        sx={{ flex: 1 }}
                      >
                        Clear Code
                      </Button>
                    </Box>
                    
                    {executionResult && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Execution Result
                        </Typography>
                        <Box 
                          sx={{ 
                            bgcolor: 'grey.100', 
                            p: 2, 
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          {executionResult}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                    <CodeIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" align="center">
                      No code generated yet. Enter a prompt and click "Generate Code" to see results.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Module2CodeGeneration;